import {groupBy, groupByUniqueKey} from '@benchling/js-utils';
import {Dispatch, SetStateAction, useState, useMemo, useEffect} from 'react';

import {SchemaFieldSelectorsDict} from 'notebook/structured-tables/hooks/useSelectorsById';
import * as ProcessManagementDAO from 'processes/dao/ProcessManagementDAO';
import {buildTableForAutomatedMappingResults} from 'processes/modals/field-mapping/buildTableForAutomatedMappingResult';
import APIUtil from 'util/APIUtil';

import {
  ProcessTask,
  ProcessOutput,
  ProcessAsyncRecord,
  SchemaMapper,
  FromItemIdsToTables,
  FixFailedRoutingAndMappingStepKey,
} from './FixFailedRoutingAndMappingConstants';
import {getSchemaMappers} from './FixFailedRoutingAndMappingUtils';

interface UseLoadMappingResultsArgs {
  currentPane: FixFailedRoutingAndMappingStepKey;
  selectedRecord: ProcessAsyncRecord | null;
  selectorsById: SchemaFieldSelectorsDict | null | undefined;
  itemIdsToMapperId: {[id: string]: Array<string>};
  itemsToMap: Array<ProcessTask> | Array<ProcessOutput> | null;
}

interface UseLoadMappingResultsResult {
  fromItemIdsToTables: FromItemIdsToTables | null;
  setFromItemIdsToTables: Dispatch<SetStateAction<FromItemIdsToTables | null>>;
  mapperIdToMapper: {[id: string]: SchemaMapper} | null;
}

/** Fetches automated mapping results from the server when the user advances to the TABLE_REVIEW step. */
export function useLoadMappingResults(args: UseLoadMappingResultsArgs): UseLoadMappingResultsResult {
  const {currentPane, selectedRecord, selectorsById, itemIdsToMapperId, itemsToMap} = args;

  const [fromItemIdsToTables, setFromItemIdsToTables] = useState<FromItemIdsToTables | null>(null);

  const mapperIdToMapper = useMemo(
    () => (selectedRecord != null ? groupByUniqueKey(getSchemaMappers(selectedRecord), ({id}) => id) : null),
    [selectedRecord]
  );

  useEffect(() => {
    // Guard against effect firing again from changed dependencies
    let cancelled = false;

    const loadAutomatedMappingResults = async (): Promise<void> => {
      const mappingResult = await ProcessManagementDAO.getAutomatedMappingResult({
        automatedMappingInputs: Object.entries(itemIdsToMapperId)
          .map(([taskOrOutputId, mapperIds]) =>
            mapperIds.map((mapperId) => ({
              mapperId,
              ...(APIUtil.isProcessTask(taskOrOutputId)
                ? {taskId: taskOrOutputId}
                : {outputId: taskOrOutputId}),
            }))
          )
          .flat(1),
      });

      if (cancelled) {
        return;
      }

      const mappingResultsByFromItemId = groupBy(mappingResult.results, ({fromItemId}) => fromItemId);

      const tempFromItemIdToTables = Object.entries(mappingResultsByFromItemId).reduce(
        (acc, [currFromItem, currMappingResults]) => {
          const resultsByMapper = groupBy(currMappingResults, (result) => result.mapperId);
          acc[currFromItem] = Object.entries(resultsByMapper).map(([mapperId, results]) => {
            const fieldsAndValues = results.map(({fields}) => fields);
            const structuredTable = buildTableForAutomatedMappingResults(
              results[0].toSchema,
              fieldsAndValues,
              selectorsById!
            );

            return {
              structuredTable,
              computedTable: new Array(structuredTable.rows.length).fill([]),
              mapper: mapperIdToMapper![mapperId],
            };
          });
          return acc;
        },
        {} as FromItemIdsToTables
      );

      // Since we also want the review pane to display items that don't map to anything, we assign
      // them a default value.
      if (itemsToMap != null) {
        for (const itemToMap of itemsToMap) {
          if (tempFromItemIdToTables[itemToMap.id] == null) {
            tempFromItemIdToTables[itemToMap.id] = [];
          }
        }
      }

      setFromItemIdsToTables(tempFromItemIdToTables);
    };

    if (currentPane === FixFailedRoutingAndMappingStepKey.TABLE_REVIEW) {
      loadAutomatedMappingResults();
    }

    return () => {
      cancelled = true;
    };
  }, [currentPane, selectorsById, itemIdsToMapperId, mapperIdToMapper, itemsToMap]);

  return {fromItemIdsToTables, setFromItemIdsToTables, mapperIdToMapper};
}
